import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { CONTACT, REALISATIONS, SERVICES, realisationDetail } from "../config/routes";
import { projects as projectsData } from "../data/projectsData";
import "./styles/Offers.css";

import servicesBackgroundPng from "../images/png/background/services_background.png";
import servicesBackgroundAvif from "../images/avif/background/services_background.avif";
import servicesBackgroundWebp from "../images/webp/background/services_background.webp";

const serviceCards = [
  {
    title: "Applications web & SaaS",
    text:
      "Plateformes web, espaces clients, abonnements, dashboards et logique métier construits autour de vrais usages.",
    deliverables: [
      "Interfaces React",
      "Authentification et roles",
      "Paiements et abonnements",
      "Backend API et base de données",
    ],
    primaryLabel: "Discuter du produit",
    primaryTo: CONTACT,
    secondaryLabel: "Voir des exemples SaaS",
    secondaryTo: REALISATIONS,
  },
  {
    title: "Dashboards & outils métiers",
    text:
      "Back-offices, outils internes et interfaces admin pour remplacer des fichiers dispersés ou des processus manuels.",
    deliverables: [
      "Gestion de données",
      "Workflows et statuts",
      "Rôles utilisateurs",
      "Automatisation de tâches",
    ],
    primaryLabel: "Parler de l'outil",
    primaryTo: CONTACT,
    secondaryLabel: "Explorer les réalisations",
    secondaryTo: REALISATIONS,
  },
  {
    title: "Applications mobiles",
    text:
      "Applications Flutter ou React Native reliées à un backend, avec comptes, données, notifications ou carte.",
    deliverables: [
      "Flutter / React Native",
      "Firebase ou Supabase",
      "Notifications et synchronisation",
      "Préparation stores si pertinent",
    ],
    primaryLabel: "Cadrer une app mobile",
    primaryTo: CONTACT,
    secondaryLabel: "Voir les projets mobiles",
    secondaryTo: REALISATIONS,
  },
  {
    title: "Automatisations & intégrations API",
    text:
      "Connexions entre outils, paiements, OAuth, emails, notifications et scripts métier pour fiabiliser les opérations.",
    deliverables: [
      "Stripe / PayPal",
      "OAuth Google et Gmail API",
      "FCM et notifications",
      "OpenAI ou API IA si utile",
    ],
    primaryLabel: "Connecter mes outils",
    primaryTo: CONTACT,
    secondaryLabel: "Voir les cas proches",
    secondaryTo: REALISATIONS,
  },
];

const fullStackItems = [
  {
    title: "Frontend",
    text: "Interfaces React, parcours responsive, tableaux de bord et composants utilisables au quotidien.",
  },
  {
    title: "Backend",
    text: "API Node.js, logique métier, authentification, permissions et traitements serveur.",
  },
  {
    title: "Base de données",
    text: "Modèles de données, relations, persistance, migrations et structure maintenable.",
  },
  {
    title: "API & intégrations",
    text: "Paiement, OAuth, Gmail API, notifications, services IA et synchronisation entre outils.",
  },
  {
    title: "Deploiement",
    text: "Mise en ligne, configuration serveur, variables d'environnement, SSL et suivi technique.",
  },
  {
    title: "Maintenance",
    text: "Corrections, évolutions, surveillance des points sensibles et documentation utile.",
  },
];

const needs = [
  "Créer un MVP exploitable pour tester une idée.",
  "Remplacer un fichier Excel par un outil métier.",
  "Développer un dashboard ou un back-office.",
  "Connecter plusieurs outils qui ne communiquent pas bien.",
  "Automatiser une tâche répétitive ou une saisie manuelle.",
  "Créer une application mobile reliée à un backend.",
  "Ajouter paiement, abonnement, comptes ou permissions.",
  "Améliorer un site ou une application existante.",
  "Créer une présence web sérieuse avec SEO local.",
];

const methodSteps = [
  {
    number: "01",
    title: "Clarification du besoin",
    text: "On identifie les utilisateurs, les données, les contraintes métier et ce qui doit vraiment être livré.",
  },
  {
    number: "02",
    title: "Priorisation MVP",
    text: "On se concentre sur le noyau utile avant d'ajouter des options qui alourdissent le projet.",
  },
  {
    number: "03",
    title: "Développement itératif",
    text: "L'interface, l'API et les données avancent ensemble pour valider rapidement les parcours.",
  },
  {
    number: "04",
    title: "Deploiement",
    text: "Le produit est mis en ligne avec une configuration claire, des variables propres et une base maintenable.",
  },
  {
    number: "05",
    title: "Suivi / maintenance",
    text: "Les retours réels servent à corriger, stabiliser et faire évoluer les prochaines versions.",
  },
];

const relatedProjectSlugs = ["echo-review", "autogestia", "hyroos", "cashping"];
const fallbackProjectSlugs = ["relyo", "trace", "psychia"];

const statusLabels = {
  production: "En production",
  mvp: "MVP",
  "in-progress": "En cours",
  prototype: "Prototype",
  "client-project": "Projet client",
};

function getProjectLabel(project) {
  return project.shortTitle || project.title;
}

function ChipList({ items, limit = 4 }) {
  if (!items?.length) return null;

  return (
    <div className="servicesV6-chipList">
      {items.slice(0, limit).map((item) => (
        <span className="servicesV6-chip" key={item}>
          {item}
        </span>
      ))}
    </div>
  );
}

function ServiceCard({ service }) {
  return (
    <article className="servicesV6-card servicesV6-serviceCard">
      <div>
        <h3>{service.title}</h3>
        <p>{service.text}</p>
      </div>

      <div>
        <p className="servicesV6-cardLabel">Exemples de livrables</p>
        <ul className="servicesV6-checkList">
          {service.deliverables.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      <div className="servicesV6-cardActions">
        <Link to={service.primaryTo} className="servicesV6-textLink">
          {service.primaryLabel}
        </Link>
        <Link to={service.secondaryTo} className="servicesV6-textLink servicesV6-textLinkMuted">
          {service.secondaryLabel}
        </Link>
      </div>
    </article>
  );
}

function ProjectCard({ project }) {
  return (
    <article className="servicesV6-card servicesV6-projectCard">
      <div className="servicesV6-projectHeader">
        <div>
          <h3>{getProjectLabel(project)}</h3>
          <p>{project.type}</p>
        </div>
        {project.status ? <span>{statusLabels[project.status] || project.status}</span> : null}
      </div>

      <p>{project.summary || project.subtitle}</p>
      <ChipList items={project.stack} />
      <Link to={realisationDetail(project.slug)} className="servicesV6-projectLink">
        Voir la fiche projet
      </Link>
    </article>
  );
}

export default function ServicesV6() {
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 599 : false
  );

  const siteOrigin = "https://dreameragency.fr";
  const siteUrl = `${siteOrigin}${SERVICES}`;
  const metaTitle = "Services - Applications web, SaaS, mobile et outils métiers";
  const metaDesc =
    "Développement d'applications web, SaaS, dashboards, applications mobiles et automatisations API : frontend, backend, bases de données, intégrations et déploiement.";
  const ogImage = `${siteOrigin}/media/OG/agency_background.png`;

  const relatedProjects = useMemo(() => {
    const preferred = relatedProjectSlugs
      .map((slug) => projectsData.find((project) => project.slug === slug))
      .filter(Boolean);

    if (preferred.length >= 4) return preferred.slice(0, 4);

    const missing = fallbackProjectSlugs
      .map((slug) => projectsData.find((project) => project.slug === slug))
      .filter((project) => project && !preferred.some((item) => item.slug === project.slug));

    return [...preferred, ...missing].slice(0, 4);
  }, []);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Développement d'applications web, SaaS, mobile et outils métiers",
      provider: {
        "@type": "Organization",
        name: "Dreamer Agency",
        url: siteOrigin,
      },
      areaServed: "France",
      url: siteUrl,
      serviceType: "Développement full-stack",
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Services Dreamer Agency",
        itemListElement: serviceCards.map((service) => ({
          "@type": "Offer",
          name: service.title,
          url: siteUrl,
          description: service.text,
        })),
      },
    }),
    [siteOrigin, siteUrl]
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();

    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }

    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".servicesV6");
    if (!mainContainer) return undefined;

    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }

    return () => {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    };
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  return (
    <main
      className={`servicesV6 ${darkMode ? "dark" : "light"}`}
      onClick={handleMainContainerClick}
    >
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={siteUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="servicesV6-hero">
        <picture className="servicesV6-heroMedia">
          <source srcSet={servicesBackgroundAvif} type="image/avif" />
          <source srcSet={servicesBackgroundWebp} type="image/webp" />
          <img src={servicesBackgroundPng} alt="" />
        </picture>
        <div className="servicesV6-heroOverlay" />

        <div className="servicesV6-container servicesV6-heroContent">
          <p className="servicesV6-eyebrow">Services freelance full-stack</p>
          <h1>Développement d'applications web, SaaS et solutions métiers</h1>
          <p className="servicesV6-lead">
            Je conçois et développe des solutions digitales complètes : interface web,
            backend, base de données, authentification, paiement, intégrations API,
            déploiement et maintenance.
          </p>
          <p className="servicesV6-heroNote">
            Une approche full-stack, pensée pour livrer un produit utilisable,
            maintenable et adapté au métier.
          </p>

          <div className="servicesV6-actions">
            <Link to={REALISATIONS} className="servicesV6-btn servicesV6-btnPrimary">
              Voir les réalisations
            </Link>
            <Link to={CONTACT} className="servicesV6-btn servicesV6-btnSecondary">
              Discuter d'un projet
            </Link>
          </div>
        </div>
      </section>

      <section className="servicesV6-section">
        <div className="servicesV6-container">
          <div className="servicesV6-sectionIntro">
            <p className="servicesV6-eyebrow">Périmètre</p>
            <h2>Ce que je peux construire</h2>
            <p>
              L'objectif n'est pas de vendre une liste de prestations abstraites, mais de
              transformer un besoin métier en produit clair : interface, données, API,
              intégrations et mise en ligne.
            </p>
          </div>

          <div className="servicesV6-servicesGrid">
            {serviceCards.map((service) => (
              <ServiceCard service={service} key={service.title} />
            ))}
          </div>
        </div>
      </section>

      <section className="servicesV6-section servicesV6-sectionAlt">
        <div className="servicesV6-container servicesV6-split">
          <div className="servicesV6-sectionIntro servicesV6-sectionIntroSticky">
            <p className="servicesV6-eyebrow">Approche full-stack</p>
            <h2>Une solution complète, pas seulement des écrans</h2>
            <p>
              Le cadrage technique reste sobre : choisir ce qui sert le produit, garder
              une base compréhensible et éviter de sur-complexifier la première version.
            </p>
          </div>

          <div className="servicesV6-stackGrid">
            {fullStackItems.map((item) => (
              <article className="servicesV6-stackItem" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="servicesV6-section">
        <div className="servicesV6-container">
          <div className="servicesV6-sectionIntro">
            <p className="servicesV6-eyebrow">Exemples liés</p>
            <h2>Des projets proches de ces besoins</h2>
            <p>
              Quelques réalisations illustrent le type de sujets traités : SaaS,
              outil métier, application mobile, API et intégrations.
            </p>
          </div>

          <div className="servicesV6-projectGrid">
            {relatedProjects.map((project) => (
              <ProjectCard project={project} key={project.slug} />
            ))}
          </div>
        </div>
      </section>

      <section className="servicesV6-section servicesV6-sectionAlt">
        <div className="servicesV6-container servicesV6-needsLayout">
          <div className="servicesV6-sectionIntro">
            <p className="servicesV6-eyebrow">Cas d'usage</p>
            <h2>Pour quels besoins ?</h2>
            <p>
              La page services reste volontairement concrète : elle doit aider à savoir
              si le besoin peut devenir une application, un dashboard ou une intégration utile.
            </p>
          </div>

          <ul className="servicesV6-needsList">
            {needs.map((need) => (
              <li key={need}>{need}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="servicesV6-section">
        <div className="servicesV6-container">
          <div className="servicesV6-sectionIntro">
            <p className="servicesV6-eyebrow">Méthode</p>
            <h2>Un déroulé simple pour avancer proprement</h2>
          </div>

          <div className="servicesV6-method">
            {methodSteps.map((step) => (
              <article className="servicesV6-methodStep" key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="servicesV6-finalCta">
        <div className="servicesV6-container servicesV6-finalCtaInner">
          <div>
            <p className="servicesV6-eyebrow">Projet web, SaaS, mobile ou outil métier</p>
            <h2>Vous avez un besoin concret à transformer en produit ?</h2>
            <p>
              Le plus utile est de clarifier le périmètre, les données et les premiers
              écrans à livrer. Ensuite, on peut décider d'une V1 raisonnable.
            </p>
          </div>
          <div className="servicesV6-actions servicesV6-actionsEnd">
            <Link to={CONTACT} className="servicesV6-btn servicesV6-btnPrimary">
              Discuter d'un projet
            </Link>
            <Link to={REALISATIONS} className="servicesV6-btn servicesV6-btnSecondary">
              Voir les réalisations
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
